import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { hasAny } from '@/utils/permissions';
import type { RoleCode } from '@/types';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    layout?: 'default' | 'blank';
    title?: string;
    roles?: RoleCode[];
    group?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { layout: 'blank', title: '登录' },
  },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/DashboardView.vue'),
    meta: { requiresAuth: true, title: '总部大盘', group: '总览' },
  },

  {
    path: '/accounts',
    name: 'accounts',
    component: () => import('@/views/accounts/AccountListView.vue'),
    meta: {
      requiresAuth: true,
      title: '账号管理',
      roles: ['super_admin', 'hq_admin'],
      group: '组织',
    },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/views/accounts/RoleMatrixView.vue'),
    meta: { requiresAuth: true, title: '角色权限', roles: ['super_admin'], group: '组织' },
  },

  {
    path: '/stores/applications',
    name: 'store-applications',
    component: () => import('@/views/stores/ApplicationListView.vue'),
    meta: {
      requiresAuth: true,
      title: '入驻审核',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '门店',
    },
  },
  {
    path: '/stores/applications/new',
    name: 'store-application-new',
    component: () => import('@/views/stores/ApplicationCreateView.vue'),
    meta: {
      requiresAuth: true,
      title: '代录申请',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '门店',
    },
  },
  {
    path: '/stores',
    name: 'stores',
    component: () => import('@/views/stores/StoreListView.vue'),
    meta: {
      requiresAuth: true,
      title: '门店列表',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '门店',
    },
  },
  {
    path: '/stores/:id',
    name: 'store-detail',
    component: () => import('@/views/stores/StoreDetailView.vue'),
    meta: {
      requiresAuth: true,
      title: '门店详情',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '门店',
    },
  },

  {
    path: '/categories',
    name: 'categories',
    component: () => import('@/views/catalog/CategoryTreeView.vue'),
    meta: {
      requiresAuth: true,
      title: '商品分类',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/products',
    name: 'products',
    component: () => import('@/views/catalog/ProductListView.vue'),
    meta: {
      requiresAuth: true,
      title: '实物商品',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/products/:id/edit',
    name: 'product-edit',
    component: () => import('@/views/catalog/ProductEditView.vue'),
    meta: {
      requiresAuth: true,
      title: '商品编辑',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/service-packages',
    name: 'service-packages',
    component: () => import('@/views/catalog/ServicePackageListView.vue'),
    meta: {
      requiresAuth: true,
      title: '服务套餐',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/pricing-tiers',
    name: 'pricing-tiers',
    component: () => import('@/views/catalog/PricingTierMatrixView.vue'),
    meta: {
      requiresAuth: true,
      title: '出货价矩阵',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/replenish/orders',
    name: 'replenish-orders',
    component: () => import('@/views/replenish/ReplenishOrderListView.vue'),
    meta: {
      requiresAuth: true,
      title: '补货单管理',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商品',
    },
  },
  {
    path: '/mall/dashboard',
    name: 'mall-dashboard',
    component: () => import('@/views/mall/MallDashboardView.vue'),
    meta: {
      requiresAuth: true,
      title: '商城概览',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商城',
    },
  },
  {
    path: '/mall/listings',
    name: 'mall-listings',
    component: () => import('@/views/mall/MallListingsView.vue'),
    meta: {
      requiresAuth: true,
      title: '商品上架',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商城',
    },
  },
  {
    path: '/mall/orders',
    name: 'mall-orders',
    component: () => import('@/views/mall/MallOrdersView.vue'),
    meta: {
      requiresAuth: true,
      title: '商城订单',
      roles: ['super_admin', 'hq_admin', 'hq_ops', 'hq_cs'],
      group: '商城',
    },
  },
  {
    path: '/mall/orders/:id',
    name: 'mall-order-detail',
    component: () => import('@/views/mall/MallOrderDetailView.vue'),
    meta: {
      requiresAuth: true,
      title: '订单详情',
      roles: ['super_admin', 'hq_admin', 'hq_ops', 'hq_cs'],
      group: '商城',
    },
  },
  {
    path: '/mall/shipments',
    name: 'mall-shipments',
    component: () => import('@/views/mall/MallShipmentsView.vue'),
    meta: {
      requiresAuth: true,
      title: '批量发货',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商城',
    },
  },
  {
    path: '/mall/after-sales',
    name: 'mall-after-sales',
    component: () => import('@/views/mall/MallAfterSalesView.vue'),
    meta: {
      requiresAuth: true,
      title: '退款售后',
      roles: ['super_admin', 'hq_admin', 'hq_ops', 'hq_cs'],
      group: '商城',
    },
  },
  {
    path: '/mall/shipping-templates',
    name: 'mall-shipping-templates',
    component: () => import('@/views/mall/MallShippingTemplatesView.vue'),
    meta: {
      requiresAuth: true,
      title: '运费模板',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '商城',
    },
  },
  {
    path: '/mall/reports',
    name: 'mall-reports',
    component: () => import('@/views/mall/MallReportsView.vue'),
    meta: {
      requiresAuth: true,
      title: '商城报表',
      roles: ['super_admin', 'hq_admin', 'hq_ops', 'hq_finance'],
      group: '商城',
    },
  },
  {
    path: '/content/articles',
    name: 'content-articles',
    component: () => import('@/views/content/ArticleListView.vue'),
    meta: {
      requiresAuth: true,
      title: '馥之道文章',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '内容运营',
    },
  },
  {
    path: '/content/articles/:id',
    name: 'content-article-edit',
    component: () => import('@/views/content/ArticleEditView.vue'),
    meta: {
      requiresAuth: true,
      title: '编辑文章',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '内容运营',
    },
  },
  {
    path: '/marketing/banners',
    name: 'marketing-banners',
    component: () => import('@/views/marketing/BannerListView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Banner 管理',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '营销',
    },
  },
  {
    path: '/marketing/promotions',
    name: 'marketing-promotions',
    component: () => import('@/views/marketing/PromotionListView.vue'),
    meta: {
      requiresAuth: true,
      title: '促销活动',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '营销',
    },
  },

  {
    path: '/devices',
    name: 'devices',
    component: () => import('@/views/devices/DeviceMapView.vue'),
    meta: {
      requiresAuth: true,
      title: '设备总览',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '设备',
    },
  },
  {
    path: '/devices/firmwares',
    name: 'firmwares',
    component: () => import('@/views/devices/FirmwareView.vue'),
    meta: {
      requiresAuth: true,
      title: 'APK 版本',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '设备',
    },
  },
  {
    path: '/devices/faults',
    name: 'faults',
    component: () => import('@/views/devices/FaultView.vue'),
    meta: {
      requiresAuth: true,
      title: '故障工单',
      roles: ['super_admin', 'hq_admin', 'hq_ops', 'hq_cs'],
      group: '设备',
    },
  },

  {
    path: '/finance/settlements',
    name: 'settlements',
    component: () => import('@/views/finance/SettlementView.vue'),
    meta: {
      requiresAuth: true,
      title: '跨店结算',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '财务',
    },
  },
  {
    path: '/finance/withdrawals',
    name: 'withdrawals',
    component: () => import('@/views/finance/WithdrawalView.vue'),
    meta: {
      requiresAuth: true,
      title: '提现审核',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '财务',
    },
  },
  {
    path: '/finance/commission-rules',
    name: 'commission-rules',
    component: () => import('@/views/finance/CommissionRuleView.vue'),
    meta: {
      requiresAuth: true,
      title: '分润规则',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '财务',
    },
  },
  {
    path: '/finance/reconcile',
    name: 'reconcile',
    component: () => import('@/views/finance/ReconcileView.vue'),
    meta: {
      requiresAuth: true,
      title: '分润对账',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '财务',
    },
  },
  {
    path: '/finance/reports',
    name: 'finance-reports',
    component: () => import('@/views/finance/ReportView.vue'),
    meta: {
      requiresAuth: true,
      title: '财务报表',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '财务',
    },
  },

  {
    path: '/referral/rules',
    name: 'referral-rules',
    component: () => import('@/views/referral/AttributionRuleView.vue'),
    meta: {
      requiresAuth: true,
      title: '推荐归属',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '推荐',
    },
  },
  {
    path: '/referral/relations',
    name: 'referral-relations',
    component: () => import('@/views/referral/ReferralTreeView.vue'),
    meta: {
      requiresAuth: true,
      title: '推荐关系',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '推荐',
    },
  },
  {
    path: '/referral/commissions',
    name: 'referral-commissions',
    component: () => import('@/views/referral/CommissionLedgerView.vue'),
    meta: {
      requiresAuth: true,
      title: '分润流水',
      roles: ['super_admin', 'hq_admin', 'hq_finance', 'hq_ops'],
      group: '推荐',
    },
  },
  {
    path: '/referral/payouts',
    name: 'referral-payouts',
    component: () => import('@/views/referral/ReferralPayoutsView.vue'),
    meta: {
      requiresAuth: true,
      title: '提现审核',
      roles: ['super_admin', 'hq_admin', 'hq_finance'],
      group: '推荐',
      permissions: ['referral:payout:read'],
    },
  },

  {
    path: '/system/configs',
    name: 'system-configs',
    component: () => import('@/views/system/ConfigView.vue'),
    meta: {
      requiresAuth: true,
      title: '全局参数',
      roles: ['super_admin', 'hq_admin'],
      group: '系统',
    },
  },
  {
    path: '/system/templates/wx',
    name: 'system-wx-templates',
    component: () => import('@/views/system/WxTemplateView.vue'),
    meta: {
      requiresAuth: true,
      title: '微信模板',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '系统',
    },
  },
  {
    path: '/system/templates/sms',
    name: 'system-sms-templates',
    component: () => import('@/views/system/SmsTemplateView.vue'),
    meta: {
      requiresAuth: true,
      title: '短信模板',
      roles: ['super_admin', 'hq_admin', 'hq_ops'],
      group: '系统',
    },
  },
  {
    path: '/system/agreements',
    name: 'system-agreements',
    component: () => import('@/views/system/AgreementView.vue'),
    meta: {
      requiresAuth: true,
      title: '协议文本',
      roles: ['super_admin', 'hq_admin'],
      group: '系统',
    },
  },
  {
    path: '/system/audit',
    name: 'system-audit',
    component: () => import('@/views/system/AuditLogView.vue'),
    meta: {
      requiresAuth: true,
      title: '审计日志',
      roles: ['super_admin', 'hq_admin'],
      group: '系统',
    },
  },
  {
    path: '/system/health',
    name: 'system-health',
    component: () => import('@/views/system/HealthView.vue'),
    meta: {
      requiresAuth: true,
      title: '系统状态',
      roles: ['super_admin', 'hq_admin'],
      group: '系统',
    },
  },
  {
    path: '/system/service-catalog',
    name: 'system-service-catalog',
    component: () => import('@/views/system/ServiceCatalogView.vue'),
    meta: {
      requiresAuth: true,
      title: '服务项目',
      roles: ['super_admin', 'hq_admin'],
      group: '系统',
    },
  },

  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const auth = useAuthStore();
    await auth.bootstrap();

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } });
    }

    if (to.name === 'login' && auth.isAuthenticated) {
      return next('/');
    }

    if (to.meta.roles && to.meta.roles.length > 0 && !hasAny(auth.roles, to.meta.roles)) {
      return next('/dashboard');
    }

    if (typeof to.meta.title === 'string') {
      document.title = `${to.meta.title} · 丝馥生 总部`;
    }

    return next();
  },
);
